<template>
  <div class="captcha-wrapper">
    <!-- 验证码容器 -->
    <div v-if="captchaData" class="captcha-container">
      <div class="captcha-header">
        <h3>请完成拼图验证</h3>
        <div class="captcha-status">
          <span v-if="isVerifying" class="verifying">验证中...</span>
          <span v-else-if="lastResult" :class="['result', lastResult.success ? 'success' : 'error']">
            {{ lastResult.message }}
          </span>
        </div>
      </div>

      <!-- 验证码主体 -->
      <div  class="captcha-body" :style="{  width: captchaData.config?.puzzle_width ? captchaData.config.puzzle_width + 300 + 'px' : 'auto', height: 'auto' }">
        <!-- 背景图容器 -->
        <div ref="captchaArea" class="background-container">
          <!-- 背景图片 -->
          <img :src="captchaData.background" alt="验证码背景" class="background-image" @load="handleImageLoad"/>
          <!-- 静态拼图块 (初始位置) -->
          <img
            v-show="!isDragging"
            :src="captchaData.puzzle"
            alt="验证拼图"
            class="puzzle-piece static"
            :style="{
              left: currentDragPosition.x + 'px',
              top: currentDragPosition.y + 'px',
              width: captchaData.config.puzzle_width + 'px',
              height: captchaData.config.puzzle_height + 'px',
              cursor: isDragging ? 'grabbing' : 'grab'
            }"
            @mousedown="startDrag"
            @touchstart="startTouchDrag"
          />
          <!-- 拖拽中的拼图块 (跟随鼠标/手指) -->
          <img
            v-show="isDragging"
            :src="captchaData.puzzle"
            alt="拖拽中"
            class="puzzle-piece dragging"
            :style="{
              left: currentDragPosition.x + 'px',
              top: currentDragPosition.y + 'px',
              width: captchaData.config.puzzle_width + 'px',
              height: captchaData.config.puzzle_height + 'px',
              cursor: 'grabbing'
            }"
          />

          <!-- 拖拽轨迹线 (视觉辅助) -->
          <svg
            v-if="isDragging && showDragLine"
            class="drag-line"
            :style="{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 5
            }"
          >
            <line
              :x1="captchaData.puzzle_position.x + captchaData.config.puzzle_width / 2"
              :y1="captchaData.puzzle_position.y + captchaData.config.puzzle_height / 2"
              :x2="currentDragPosition.x + captchaData.config.puzzle_width / 2"
              :y2="currentDragPosition.y + captchaData.config.puzzle_height / 2"
              stroke="#4CAF50"
              stroke-width="2"
              stroke-dasharray="5,5"
            />
          </svg>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-else-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>正在加载验证码...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="loadError" class="error-container">
      <div class="error-icon">⚠</div>
      <p>验证码加载失败</p>
      <button class="btn btn-retry" @click="loadCaptcha">重试</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SlideCaptcha',
  
  props: {
    // API配置
    generateApi: {
      type: String,
      default: 'captcha/generate'
    },
    verifyApi: {
      type: String,
      default: 'captcha/refresh'
    },
    // 自动加载
    autoLoad: {
      type: Boolean,
      default: false
    },
    phone: {
      type: String,
      default: ''
    },
    type: {
      type: Number,
      default: 0
    }
  },
  watch: {
    autoLoad: {
      deep: true,
      handler() {
        if (this.autoLoad) {
          this.loadCaptcha()
        }
      }
    }
  },
  data() {
    return {
      // 验证码数据
      captchaData: null,
      captchaKey: '',
      
      // 拖拽状态
      isDragging: false,
      hasDragged: false,
      startDragPosition: { x: 0, y: 0 },
      currentDragPosition: { x: 0, y: 0 },
      dragOffset: { x: 0, y: 0 },
      
      // 验证状态
      isVerifying: false,
      isRefreshing: false,
      lastResult: null,
      
      // 加载状态
      isLoading: false,
      loadError: false,
      
      // 配置选项
      showTargetHint: true,
      showDragLine: true,
      showDebugInfo: true, // 调试信息默认打开
      
      // 图片加载状态
      imageLoaded: false
    }
  },
  computed: {
    userIP() { return this.$store.state.userIP}
  },
  mounted() {
    if (this.autoLoad) {
      this.loadCaptcha()
    }
  },
  beforeDestroy() {
    // 清理事件监听
    this.removeEventListeners()
  },
  
  methods: {
    // 加载验证码
    async loadCaptcha() {
      this.isLoading = true
      this.loadError = false
      this.lastResult = null
      
      try {
        // 使用图片中的数据结构
        const response = await this.$api.get(this.generateApi)
        
        if (response.code === 200) {
          this.captchaData = response.data
          this.captchaKey = response.data.captcha_key
          
          // 初始化位置
          this.currentDragPosition = {
            x: response.data.puzzle_position.x,
            y: response.data.puzzle_position.y
          }
          
          this.hasDragged = false
        } else {
          throw new Error(response.data.message || '验证码加载失败')
        }
      } catch (error) {
        this.loadError = true
        this.$emit('error', error)
      } finally {
        this.isLoading = false
      }
    },
    
    // 图片加载完成
    handleImageLoad() {
      this.imageLoaded = true
    },
    
    // 开始拖拽 (鼠标)
    startDrag(event) {
      if (!this.captchaData) return
      
      event.preventDefault()
      event.stopPropagation()
      
      this.isDragging = true
      
      // 获取拼图块元素位置
      const rect = event.target.getBoundingClientRect()
      
      // 获取容器位置
      const container = this.$refs.captchaArea
      const containerRect = container.getBoundingClientRect()
      
      // 计算鼠标在拼图块内的偏移
      this.dragOffset = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      }
      
      this.startDragPosition = {
        x: this.currentDragPosition.x,
        y: this.currentDragPosition.y
      }
      
      // 添加全局事件监听
      document.addEventListener('mousemove', this.handleDrag)
      document.addEventListener('mouseup', this.endDrag)
    },
    
    // 开始拖拽 (触摸)
    startTouchDrag(event) {
      if (!this.captchaData) return
      
      event.preventDefault()
      event.stopPropagation()
      
      this.isDragging = true
      
      const touch = event.touches[0]
      const rect = event.target.getBoundingClientRect()
      const container = this.$refs.captchaArea
      const containerRect = container.getBoundingClientRect()
      
      this.dragOffset = {
        x: touch.clientX - containerRect.left - rect.left,
        y: touch.clientY - containerRect.top - rect.top
      }
      
      this.startDragPosition = {
        x: this.currentDragPosition.x,
        y: this.currentDragPosition.y
      }
      
      // 添加触摸事件监听
      document.addEventListener('touchmove', this.handleTouchDrag, { passive: false })
      document.addEventListener('touchend', this.endDrag)
      document.addEventListener('touchcancel', this.endDrag)
    },
    // 处理拖拽 (鼠标)
    handleDrag(event) {
      if (!this.isDragging || !this.captchaData) return
      event.preventDefault()
      const container = this.$refs.captchaArea
      if (!container) return
      
      const containerRect = container.getBoundingClientRect()
      const backgroundImage = container.querySelector('.background-image')
      
      if (!backgroundImage) return
      
      // 获取背景图片的实际显示尺寸
      const bgRect = backgroundImage.getBoundingClientRect()
      
      // 计算新位置（相对于容器）
      let newX = event.clientX - containerRect.left - this.dragOffset.x
      let newY = this.currentDragPosition.y
      
      // 限制在背景图范围内
      const maxX = bgRect.width - this.captchaData.config.puzzle_width
      
      
      // 确保位置在有效范围内
      newX = Math.max(0, Math.min(newX, maxX))
      
      
      this.currentDragPosition = { x: newX, y: newY }
      this.hasDragged = true
    },
    
    // 处理拖拽 (触摸)
    handleTouchDrag(event) {
      if (!this.isDragging || !this.captchaData) return
      
      event.preventDefault()
      
      const container = this.$refs.captchaArea
      if (!container) return
      
      const touch = event.touches[0]
      const containerRect = container.getBoundingClientRect()
      const backgroundImage = container.querySelector('.background-image')
      
      if (!backgroundImage) return
      
      const bgRect = backgroundImage.getBoundingClientRect()
      
      // 计算新位置
      let newX = touch.clientX - containerRect.left - this.dragOffset.x
      let newY = this.currentDragPosition.y
      
      // 限制在背景图范围内
      const maxX = bgRect.width - this.captchaData.config.puzzle_width
      
      newX = Math.max(0, Math.min(newX, maxX))
      
      this.currentDragPosition = { x: newX, y: newY }
      this.hasDragged = true
    },
    
    // 结束拖拽
    endDrag(event) {
      this.isDragging = false
      // 移除事件监听
      this.removeEventListeners()
      this.submitVerification()
    },
    
    // 移除事件监听
    removeEventListeners() {
      document.removeEventListener('mousemove', this.handleDrag)
      document.removeEventListener('mouseup', this.endDrag)
      document.removeEventListener('touchmove', this.handleTouchDrag)
      document.removeEventListener('touchend', this.endDrag)
      document.removeEventListener('touchcancel', this.endDrag)
    },
    
    // 重置拖拽位置
    resetDrag() {
      if (!this.captchaData) return
      this.currentDragPosition = {
        x: this.captchaData.puzzle_position.x,
        y: this.captchaData.puzzle_position.y
      }
      this.hasDragged = false
      this.lastResult = null
    },
    // 提交验证
    async submitVerification() {
      try {
        let type = this.type === 2 ? 1 : 2
        const verifyData = {
          phone: this.phone,
          ip: this.userIP,
          type: type,
          captcha_key: this.captchaKey,
          drag_x: this.currentDragPosition.x
        }
        const res = await this.$api.post('student_new/sms_send', verifyData)
        if (res.code === 200) {
          this.$message.success(res.message)
          this.$emit('overVerify')
        } else {
          this.resetDrag()
          this.$message.error(res.message)
        }
      } catch (error) {
        this.resetDrag()
        this.$message.error(res.message)
      }
    },
  }
}
</script>

<style scoped>
.captcha-wrapper {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  max-width: 600px;
  margin: 0 auto;
}

.captcha-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 20px;
}

.captcha-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.captcha-header h3 {
  margin: 0;
  color: #333;
  font-weight: 600;
}

.captcha-status {
  font-size: 14px;
}

.verifying {
  color: #666;
}

.result {
  font-weight: 500;
}

.result.success {
  color: #4CAF50;
}

.result.error {
  color: #f44336;
}

.captcha-body {
  position: relative;
  margin-bottom: 20px;
  user-select: none;
}

.background-container {
  position: relative;
  display: inline-block;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  cursor: default;
  user-select: none;
  touch-action: none; /* 防止触摸默认行为 */
}

.background-image {
  display: block;
  height: auto;
  pointer-events: none;
}

.target-hint {
  position: absolute;
  border: 2px dashed #4CAF50;
  border-radius: 4px;
  box-sizing: border-box;
  pointer-events: none;
  z-index: 5;
  background: rgba(76, 175, 80, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.hint-text {
  color: #4CAF50;
  font-size: 12px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 6px;
  border-radius: 3px;
}

.puzzle-piece {
  position: absolute;
  z-index: 10;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: box-shadow 0.2s;
  user-select: none;
}

.puzzle-piece.static {
  cursor: grab;
  pointer-events: auto;
}

.puzzle-piece.dragging {
  cursor: grabbing;
  z-index: 20;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  pointer-events: none; /* 防止拖拽时触发其他事件 */
}

.debug-info {
  margin-top: 10px;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
  font-family: 'Courier New', monospace;
}

.debug-info p {
  margin: 5px 0;
}

.captcha-controls {
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.control-group {
  display: grid;
  flex-wrap: wrap;
  gap: 15px;
  grid-auto-flow:column;grid-auto-columns:max-content;justify-content:start;
  margin-bottom: 20px;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 6px;
}

.control-label {
  display: grid;
  grid-auto-flow:column;grid-auto-columns:max-content;justify-content:start;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #555;
  cursor: pointer;
}

.control-label input[type="checkbox"] {
  cursor: pointer;
}

.button-group {
  display: grid;
  gap: 10px;
  margin-bottom: 20px;
  grid-auto-flow:column;grid-auto-columns:max-content;justify-content:start;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #2196F3;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #1976D2;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
}

.btn-secondary:hover:not(:disabled) {
  background: #e0e0e0;
}

.btn-refresh {
  background: #FF9800;
  color: white;
}

.btn-refresh:hover:not(:disabled) {
  background: #F57C00;
}

.btn-retry {
  background: #4CAF50;
  color: white;
  margin-top: 10px;
}

.result-message {
  display: grid;
  align-items: flex-start;
  gap: 12px;
  grid-auto-flow:column;grid-auto-columns:max-content;justify-content:start;
  padding: 15px;
  border-radius: 8px;
  animation: fadeIn 0.3s ease;
}

.result-message.success {
  background: rgba(76, 175, 80, 0.1);
  border-left: 4px solid #4CAF50;
}

.result-message.error {
  background: rgba(244, 67, 54, 0.1);
  border-left: 4px solid #f44336;
}

.result-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
}

.result-message.success .result-icon {
  background: #4CAF50;
  color: white;
}

.result-message.error .result-icon {
  background: #f44336;
  color: white;
}

.result-text {
  flex: 1;
}

.result-text strong {
  display: block;
  margin-bottom: 4px;
  font-size: 16px;
}

.result-text p {
  margin: 0 0 4px 0;
  font-size: 14px;
  color: #666;
}

.result-text small {
  font-size: 12px;
  color: #999;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #2196F3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.error-icon {
  font-size: 48px;
  color: #f44336;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* 响应式设计 */
@media (max-width: 640px) {
  .captcha-container {
    padding: 15px;
  }
  
  .button-group {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
  
  .control-group {
    flex-direction: column;
    gap: 10px;
  }
}
</style>